import React, { createContext, useState, useEffect, useContext } from 'react';

// Define theme colors
export const lightTheme = {
  primary: '#FFFFFF',
  secondary: '#6200EE',
  accent: '#00E5FF', // Electric teal
  accentGradient: 'linear-gradient(135deg, #00E5FF 0%, #0088FF 100%)',
  background: '#FFFFFF',
  backgroundAlt: '#F8F9FA',
  surface: '#F5F5F5',
  surfaceAlt: 'rgba(255, 255, 255, 0.8)',
  text: '#212121',
  textSecondary: '#757575',
  border: '#EEEEEE',
  card: '#FFFFFF',
  cardHover: '#F5F5F5',
  shadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  shadowHover: '0 10px 25px rgba(0, 0, 0, 0.12)',
  success: '#4CAF50',
  error: '#F44336',
  warning: '#FF9800',
  info: '#2196F3',
  overlay: 'rgba(0, 0, 0, 0.5)',
  glass: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  glassHighlight: 'rgba(255, 255, 255, 0.9)',
};

export const darkTheme = {
  primary: '#121212',
  secondary: '#BB86FC',
  accent: '#00E5FF', // Electric teal
  accentGradient: 'linear-gradient(135deg, #00E5FF 0%, #0088FF 100%)',
  background: '#121212',
  backgroundAlt: '#1E1E1E',
  surface: '#1E1E1E',
  surfaceAlt: 'rgba(30, 30, 30, 0.8)',
  text: '#FFFFFF',
  textSecondary: '#BBBBBB',
  border: '#333333',
  card: '#1E1E1E',
  cardHover: '#252525',
  shadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  shadowHover: '0 10px 25px rgba(0, 0, 0, 0.3)',
  success: '#4CAF50',
  error: '#F44336',
  warning: '#FF9800',
  info: '#2196F3',
  overlay: 'rgba(0, 0, 0, 0.7)',
  glass: 'rgba(30, 30, 30, 0.7)',
  glassBorder: 'rgba(255, 255, 255, 0.1)',
  glassHighlight: 'rgba(255, 255, 255, 0.05)',
};

// Create context
const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Check if user has a theme preference in localStorage
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    // Check if user prefers dark mode
    return window.matchMedia && 
      window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? 'dark' 
      : 'light';
  };

  const [theme, setTheme] = useState('dark'); // Default to dark theme
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Set theme on initial load
  useEffect(() => {
    const initialTheme = getInitialTheme();
    setTheme(initialTheme);
    setThemeLoaded(true);
  }, []);

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    if (themeLoaded) {
      localStorage.setItem('theme', theme);
      // Apply theme to body
      document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute(
          'content',
          theme === 'dark' ? darkTheme.background : lightTheme.background
        );
      }
    }
  }, [theme, themeLoaded]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Get current theme colors
  const colors = theme === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};